import { InsightsSupplier } from '@shared/api/types';
import Text from '@shared/components/content/text';
import { CardPager } from '@shared/components/dashboard/card-pager';
import { formatCompact, getCurrencySymbol } from '@shared/lib/format';
import { useState } from 'react';
import { DrillthroughLink } from './drillthrough-link';
import { ItemPanel } from './item-panel';

const PAGE_SIZE = 3;

type Props = {
  suppliers?: readonly InsightsSupplier[];
  /** Purchases in the window — distinguishes "no purchases" from purchases
   *  that simply carry no supplier information. */
  purchases?: number | null;
  currency?: string | null;
  /** Link target for the drill-throughs (the Purchases tab). */
  purchasesHref: string;
  /** How a supplier's quantity reads when it has no spend. */
  quantityUnit: 'kg' | 'units';
  /** Without the cost feature, rows show purchases and quantity, never spend. */
  hasCostFeature: boolean;
};

/**
 * The suppliers of this purchased good within the page's period and site
 * scope, with their purchase counts and spend (or quantity when no spend is
 * recorded).
 */
export function SuppliedByPanel({
  suppliers = [],
  purchases,
  currency,
  purchasesHref,
  quantityUnit,
  hasCostFeature,
}: Props) {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(suppliers.length / PAGE_SIZE);
  const pageSuppliers = suppliers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const rowCaption = (supplier: InsightsSupplier) => {
    const parts: string[] = [];
    if (supplier.purchases != null) {
      const noun = supplier.purchases === 1 ? 'purchase' : 'purchases';
      parts.push(`${supplier.purchases.toLocaleString('en-GB')} ${noun}`);
    }
    const spend =
      hasCostFeature && supplier.totalSpend != null
        ? Number(supplier.totalSpend)
        : null;
    if (spend != null) {
      const symbol = getCurrencySymbol(currency) || '£';
      parts.push(`${symbol}${Math.round(spend).toLocaleString('en-GB')} spent`);
    } else if (supplier.quantity != null) {
      parts.push(`${formatCompact(supplier.quantity)} ${quantityUnit}`);
    }
    return parts.join(', ');
  };

  return (
    <ItemPanel
      title="Supplied by"
      headerAction={
        <DrillthroughLink to={purchasesHref}>View purchases</DrillthroughLink>
      }
      footer={
        <CardPager
          page={page}
          pageCount={pageCount}
          onPageChange={setPage}
          className="mt-2 justify-end"
        />
      }
    >
      {suppliers.length === 0 ? (
        <div className="flex h-full min-h-24 items-center justify-center">
          <Text variant="subtle">
            {purchases
              ? 'No supplier data for these purchases'
              : 'No purchases in this period'}
          </Text>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {pageSuppliers.map((supplier) => (
            <li
              key={supplier.uuid}
              className="flex min-h-16 items-center justify-between gap-3 py-3"
            >
              <Text variant="body" className="truncate">
                {supplier.name}
              </Text>
              <Text variant="subtle" className="shrink-0">
                {rowCaption(supplier)}
              </Text>
            </li>
          ))}
        </ul>
      )}
    </ItemPanel>
  );
}
